/**
 * @file        src/pages/platform/TenantCompanyPage.jsx
 * @description Tenant Management → Issuing Company. The selected tenant's
 *              company as printed on its tax invoices, e-tickets and hotel
 *              vouchers, written into the tenant DB's site_settings row
 *              (migration 051). ?tenant=<id> picks the tenant.
 *
 *              This is the super admin's ONBOARDING path, not an exclusive one:
 *              the tenant admin edits the same fields later under Site
 *              Management → Site Setting → Issuing company. It has to exist
 *              because go-live readiness now requires the company, and a
 *              pending tenant's own host 404s (resolveTenant serves only
 *              'active') — so without a path from the platform host, activation
 *              would deadlock on a field only the un-loggable tenant could set.
 *
 *              Unlike Admin Portal Branding, these details are NOT decorative:
 *              they are the issuer on a financial document, so leaving them
 *              unset prints '—' on a real customer's tax invoice rather than
 *              falling back to anything.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (issuing company was hardcoded to Neon in the
 *                          admin frontend, so every tenant's documents named
 *                          tenant 1's company).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import { get, put } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import TenantPicker, { useTenantParam } from './TenantPicker';
import Field from './Field';

const EMPTY = {
  company_name: '', company_legal_name: '', address: '', city: '', state: '',
  country: '', pincode: '', phone: '', email: '', support_email: '', website: '',
  jurisdiction: '',
};

export default function TenantCompanyPage() {
  const [tenantId, setTenantId] = useTenantParam();
  return (
    <>
      <PageHead title="Issuing Company" subtitle="Platform · Tenant Management · invoice & ticket issuer" />
      <article className="panel glass" style={{ marginBottom: 16 }}>
        <TenantPicker value={tenantId} onChange={setTenantId} />
      </article>
      {tenantId ? <TenantCompany tenantId={tenantId} /> : (
        <article className="panel glass"><div className="empty">Select a tenant to set its issuing company.</div></article>
      )}
    </>
  );
}

function TenantCompany({ tenantId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.tenants.company(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/company`).then((r) => r.data),
  });

  useEffect(() => {
    if (data) setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] || ''])));
  }, [data]);

  const mut = useMutation({
    mutationFn: (body) => put(`/tenants/${tenantId}/company`, body),
    onSuccess: () => {
      toast.ok('Company saved', "This tenant's invoices and tickets now carry these details.");
      qc.invalidateQueries({ queryKey: qk.tenants.company(tenantId) });
      // The go-live checklist gates on the legal name + address.
      qc.invalidateQueries({ queryKey: qk.tenants.readiness(tenantId) });
    },
    onError: (e) => toast.err('Could not save', e.response?.data?.error?.message || 'Failed'),
  });

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <article className="panel glass">
      <div className="panel-head">
        <div>
          <h2>Issuing Company</h2>
          <p className="muted">
            Printed as the issuer on this tenant's tax invoices, e-tickets and hotel vouchers, and
            used to sign the emails that send them. The registered legal name and address are
            required before the tenant can go live — a document with no issuer is not a valid tax
            invoice. The tenant's own admin can edit these later under Site Setting.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Loading company details…</div>
      ) : isError ? (
        <div className="empty"><p className="field-error">Could not load this tenant's company details.</p></div>
      ) : (
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); mut.mutate(form); }}>
          <Field label="Company Name">
            <input value={form.company_name} onChange={on('company_name')} placeholder="Trading name, e.g. Beyond Destination" />
          </Field>
          <Field label="Registered Legal Name" required hint="Shown in the invoice “From” block.">
            <input value={form.company_legal_name} onChange={on('company_legal_name')} placeholder="e.g. Beyond Destination Pvt. Ltd." />
          </Field>
          <Field label="Address" required full>
            <textarea rows={2} value={form.address} onChange={on('address')} placeholder="Registered office address" />
          </Field>
          <Field label="City"><input value={form.city} onChange={on('city')} placeholder="City" /></Field>
          <Field label="State"><input value={form.state} onChange={on('state')} placeholder="State" /></Field>
          <Field label="Country"><input value={form.country} onChange={on('country')} placeholder="Country" /></Field>
          <Field label="Pincode"><input value={form.pincode} onChange={on('pincode')} placeholder="Pincode" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={on('phone')} placeholder="+91 …" /></Field>
          <Field label="Email"><input value={form.email} onChange={on('email')} placeholder="contact@…" /></Field>
          <Field label="Support Email"><input value={form.support_email} onChange={on('support_email')} placeholder="support@…" /></Field>
          <Field label="Website"><input value={form.website} onChange={on('website')} placeholder="www.example.com" /></Field>
          <Field label="Legal Jurisdiction" hint="Named in the invoice terms (“disputes subject to … jurisdiction”).">
            <input value={form.jurisdiction} onChange={on('jurisdiction')} placeholder="e.g. Mumbai, India" />
          </Field>
          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="check" /> {mut.isPending ? 'Saving…' : 'Save Company'}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
